import { streamSheetColumns } from './streamSheetColumns';
import type { LoadLine, SpaListLine } from './unloadedSpaReport';

/**
 * The two workbooks the report reads, by header. Column C (BRANCH INFO) is
 * the branch and banner; END CUSTOMER ACCOUNT NO is ABB's number, not Rexel's.
 */
export const SPA_LIST_HEADERS = [
  'QUOTE',
  'BRANCH INFO',
  'QUOTE DATE',
  'VALID FROM',
  'VALID TO',
  'REVISION NUM',
  'END CUSTOMER NAME',
  'END CUSTOMER ACCOUNT NO',
] as const;

/** The Results Workbook columns read; ABB puts a UoM row above the header. */
export const LOAD_HEADERS = [
  'Division',
  'QUOTE SPA',
  'Account',
  'UNIQUE TITLE',
] as const;

const HEADER_ROWS = 3;

export type UnloadedSpaInput = 'spa list' | 'results';

/** The file is not the input it was dropped as: none of its sheets carries the header. */
export class MissingHeaderError extends Error {
  constructor(readonly input: UnloadedSpaInput) {
    super(`No sheet carries the ${input} header.`);
  }
}

/** Trimmed: the Results Workbook pads Account (" 1509"). Leading zeros are kept. */
function cell(row: readonly string[], index: number): string {
  return (row[index] ?? '').trim();
}

export async function readSpaList(file: Blob): Promise<SpaListLine[]> {
  const sheet = await streamSheetColumns(file, {
    headers: SPA_LIST_HEADERS,
    headerRows: HEADER_ROWS,
  });
  if (!sheet) throw new MissingHeaderError('spa list');
  const lines: SpaListLine[] = [];
  for (const row of sheet.rows) {
    const quote = cell(row, 0);
    const branch = cell(row, 1);
    if (quote === '' || branch === '') continue;
    lines.push({
      quote,
      branch,
      quoteDate: cell(row, 2),
      validFrom: cell(row, 3),
      validTo: cell(row, 4),
      revision: cell(row, 5),
      endCustomerName: cell(row, 6),
      endCustomerNumber: cell(row, 7),
    });
  }
  return lines;
}

export async function readLoads(file: Blob): Promise<LoadLine[]> {
  const sheet = await streamSheetColumns(file, {
    headers: LOAD_HEADERS,
    headerRows: HEADER_ROWS,
  });
  if (!sheet) throw new MissingHeaderError('results');
  // One pass: the Results Workbook is 272k lines.
  const lines: LoadLine[] = [];
  for (const row of sheet.rows) {
    const division = cell(row, 0);
    const quote = cell(row, 1);
    if (quote === '' || division === '') continue;
    lines.push({ division, quote, account: cell(row, 2), title: cell(row, 3) });
  }
  return lines;
}
